import { useRef } from "react";
import FallbackPaywall from "@blocks/FallbackPaywall";
import DynamicImu from "@components/Advertisement/DynamicImu";
import CASBlock from "@components/CASBlock";
import Container from "@components/Container/Container";
import DelayedComponentsLocationChange from "@components/DelayedComponents/DelayedComponentsLocationChange";
import GAData from "@components/GAData/GAData";
import {
  getArticleAuthorNamesV2,
  getArticleKeywords,
  getArticleStoryThread,
} from "@components/GAData/helper";
import { renderPageTitleV2 } from "@components/MetaTags/helpers";
import MetaTags from "@components/MetaTags/MetaTags";
import PosthogFeature from "@components/Posthog/PosthogFeature";
import ProgressBar from "@components/ProgressBar/ProgressBar";
import ArticleToolbar from "@features/toolbar/ArticleToolbar/ArticleToolbar";
import { useCountry } from "@hooks/useCountry";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import useIsAppWebView from "@hooks/useIsAppWebView";
import { useMostReadArticleList } from "@hooks/useMostReadArticleList";
import usePublishedTime from "@hooks/usePublishedTime";
import ArticleAttachments from "@pages/Article/components/ArticleAttachments";
import ArticleByline from "@pages/Article/components/ArticleByline/ArticleByline";
import ArticleKicker from "@pages/Article/components/ArticleKicker";
import ArticleMainMedia from "@pages/Article/components/ArticleMainMedia/ArticleMainMedia";
import ArticleParagraphElements from "@pages/Article/components/ArticleParagraphElements/ArticleParagraphElements";
import { replaceProblematicSymbols } from "@pages/Article/components/ArticleParagraphElements/utils/helpers";
import ArticlePurchaseLink from "@pages/Article/components/ArticlePurchaseLink/ArticlePurchaseLink";
import { Versions } from "@pages/Article/components/ArticlePurchaseLink/types";
import ArticlesFromSameSection from "@pages/Article/components/ArticlesFromSameSection/ArticlesFromSameSection";
import ArticleSubscriberLabel from "@pages/Article/components/ArticleSubscriberLabel";
import ArticleSubshareSent from "@pages/Article/components/ArticleSubshare/ArticleSubshareSent";
import ArticleTags from "@pages/Article/components/ArticleTags";
import ChineseBanner from "@pages/Article/components/ChineseBanner";
import FeedbackCN from "@pages/Article/components/Feedback/FeedbackCN";
import OutBrainWidget from "@pages/Article/components/OutBrainWidget";
import { useHookUpdateUrlArticle } from "@pages/Article/hooks/useHookUpdateUrlArticle";
import { ArticleAuthorType, VariantType } from "@pages/Article/utils/enums";
import {
  getFirstParagraphText,
  getIsBrandedContent,
  getUrlPathFromAbsoluteUrl,
} from "@pages/Article/utils/helpers";
import { CommonArticleLayoutProps } from "@pages/Article/utils/types";
import MostPopularNew from "@pages/Section/components/MostPopular/MostPopularNew";
import useMyBtStore from "@store/useMyBtStore";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { AccessFlag } from "@typings/enums";
import {
  CDP_PAYWALL_180_DAYS_MODULE_ID,
  CDP_PAYWALL_MODULE_ID,
  dayjsSingaporeTimezone,
} from "@util/constant";
import {
  checkIfFromPaidPressRelease,
  cn,
  getArticleJSONLD,
} from "@util/helpers";
import dayjs from "dayjs";
import {
  useFeatureFlagEnabled,
  useFeatureFlagVariantKey,
} from "posthog-js/react";

const MainNewsDefault = ({
  article,
  index = 0,
  gsChannels,
  handleGenerateGiftURLModal,
  setIsModalOpen,
  alacrityRes,
  isModalOpen = false,
  setArticleObj,
  articleInView,
  displayArticleRef,
  isGifted = false,
  isPreview = false,
  isOutbrainEnabled,
  readMoreSectionOrTagData,
  isNavigate = false,
  isSummaryAvailable = false,
  isChinese = false,
}: CommonArticleLayoutProps): React.ReactElement => {
  // De-structure of article so it is cleaner to get the fields.
  const {
    id,
    title,
    kicker,
    paidMode,
    blurb,
    authors,
    publishTime,
    editedTime,
    hasUpdatedTimeDisplayed,
    media,
    relatedAttachments,
    sections,
    tags,
    seoFields,
    copyright,
    standfirst,
  } = article;

  const urlPath = getUrlPathFromAbsoluteUrl(article.urlPath || "");

  const outbrainWidgetId = index === 0 ? "AR_4" : "AR_5";
  const testOutbrainWidgetId =
    urlPath ===
    "/startups-tech/technology/dell-gains-after-beating-sales-estimates-on-demand-for-tech-services"
      ? "AR_10"
      : outbrainWidgetId;

  const variant = useFeatureFlagVariantKey(
    "feature_load_more_article_logic"
  )?.toString();

  const isUserLoginEnabled = useFeatureFlagEnabled("feature_enable_user_login");
  const isFeature60DaysAgo = useFeatureFlagEnabled("feature_60_days_ago");
  const { isMoreThan60DaysAgo } = usePublishedTime(article?.publishTime);
  const removeMoreVariant = useFeatureFlagVariantKey(
    "feature_remove_more_sections"
  )?.toString();

  const OktaUserInfo = useOKTAUserStore((store) => store.userInfo);
  const { ref } = useHookUpdateUrlArticle(
    urlPath,
    isNavigate,
    article,
    variant,
    setArticleObj
  );

  const { gsChannels: gsChannelsAPI } = useGrapeshotData(urlPath, index);
  const isAppWebView = useIsAppWebView();
  const mostPopularData = useMostReadArticleList();
  const { isSGUser } = useCountry();

  const isPremium = paidMode === "premium";
  const mainSection = sections?.[0];
  const parentSection = mainSection?.parent || mainSection;
  const myBTTags = useMyBtStore((store) => store.tags);
  const firstParagraph = getFirstParagraphText(article.body || []);

  const articleContentProgress = useRef<HTMLDivElement>(null);

  const isBrandedContent = getIsBrandedContent(article?.kicker || "");
  const isPaidPressRelease = checkIfFromPaidPressRelease(
    article?.sections || []
  );

  const purchaseArticleDetails = {
    isDisplayed: copyright === "SPH",
    publishedDate: publishTime,
    title,
  };

  const isSubscriber = OktaUserInfo?.usertype === OKTAUserTypeEnum.SUBSCRIBER;

  const truncated60DaysAgo =
    isFeature60DaysAgo && !isSubscriber && !isPremium && isMoreThan60DaysAgo;

  const isFullContent =
    !isSGUser ||
    isSubscriber || // User is a subscriber
    isGifted || // Article is gifted
    isPreview || // Article is in preview mode
    !isUserLoginEnabled || // Feature flag is disabled
    (!truncated60DaysAgo && !isPremium); // Feature truncated180DaysAgo is false

  const cdpPaywallModuleId = truncated60DaysAgo
    ? CDP_PAYWALL_180_DAYS_MODULE_ID
    : CDP_PAYWALL_MODULE_ID;

  const paragraphIndices = article.body
    .map((content, idx) => (content.startsWith("<p>") ? idx : null))
    .filter((idx) => idx !== null);
  const totalParagraphs = paragraphIndices.length;

  const isAuthorOnly = authors?.some(
    (author) => author.type === ArticleAuthorType.AUTHOR_PROFILE
  );

  return (
    <>
      <GAData
        articleid={id}
        author={getArticleAuthorNamesV2(authors)}
        chapter1={mainSection.uniqueName}
        contentcat={isPremium ? 2 : 1}
        contenttype="1"
        keyword={getArticleKeywords(tags || [])}
        level2={parentSection.uniqueName}
        pubdate={dayjs(article.publishTime)
          .tz(dayjsSingaporeTimezone)
          .format("YYYY-MM-DD HH:mm:ss")}
        title={replaceProblematicSymbols(title)}
        product_flag={myBTTags.length > 0 ? "mybt" : "(not set)"}
        user_keyword={
          myBTTags.length > 0
            ? myBTTags.map((tag) => tag.name).join("|")
            : "(not set)"
        }
        story_threads={getArticleStoryThread(tags || [])?.name.toLowerCase()}
        gsChannels={index === 0 ? gsChannels : gsChannelsAPI}
        abVariant={removeMoreVariant || "control"}
        language={isChinese ? "sc" : "en"}
      />

      <MetaTags
        title={renderPageTitleV2({
          kind: "art",
          title: seoFields?.metaTitle || title,
        })}
        author={getArticleAuthorNamesV2(authors)}
        articleId={id}
        description={standfirst || blurb || firstParagraph || ""}
        keywords={article?.tags?.map((tag) => tag.name)}
        slug={urlPath}
        canonicalUrl={seoFields?.canonicalUrl}
        imageUrl={media?.[0]?.original?.url}
        imageAlt={media?.[0]?.caption || title}
        jsonLd={getArticleJSONLD(article)}
        robots={"index, follow, max-image-preview:large"}
        gsChannels={index === 0 ? gsChannels : gsChannelsAPI}
        articleCategory={parentSection.name}
        accessFlag={isPremium ? AccessFlag.Premium : AccessFlag.Free}
        publishedDate={publishTime}
        updatedDate={editedTime}
        ogTitle={seoFields?.ogTitle}
        ogDescription={seoFields?.ogDescription}
        ogImageUrl={seoFields?.ogImages?.[0]?.original?.url}
      />

      <ArticleSubshareSent
        alacrityRes={alacrityRes}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />

      <ProgressBar
        articleContentProgress={articleContentProgress}
        className={cn("top-[60px]")}
        isDisplayed={!isAppWebView && articleInView.id === article.id}
      />

      <article
        className={cn(
          "display-type--mainNewsDefault w-full py-5",
          "animate-slideIn"
        )}
        data-index={index}
        data-cueid={id}
        data-url-path={urlPath}
        ref={ref}
      >
        <div ref={articleContentProgress} className="relative">
          <Container rootClassName="md:pt-4">
            <Container rootClassName="px-0 mx-auto md:px-3 w-full md:w-10/12 lg:w-8/12 mb-4">
              <>
                {isPremium || kicker ? (
                  <div className="flex flex-wrap items-center pb-2 md:px-8 md:pb-4">
                    <ArticleKicker
                      kicker={kicker}
                      className="font-base font-light uppercase"
                    />

                    {isPremium && kicker ? (
                      <div
                        className="ml-3 mr-2 font-poppins text-2xl font-light leading-normal tracking-[0.075rem] text-gray-650"
                        data-testid="kicker-subscriber-label-separator"
                      >
                        ·
                      </div>
                    ) : null}

                    <ArticleSubscriberLabel
                      isPremium={isPremium}
                      displayDot={false}
                      rootClassName={"font-base font-light"}
                    />
                  </div>
                ) : null}
              </>

              <h1
                data-testid="article-title"
                className="mb-2 font-lct text-4xl font-bold !leading-[1.3] tracking-[-0.045rem] text-gray-850 md:px-8 md:text-10xl"
              >
                {title}
              </h1>

              <>
                {standfirst ? (
                  <p
                    className={cn(
                      "mb-4 mt-2 font-lucida text-lg font-medium !leading-8 -tracking-5% text-gray-850 md:px-8 md:text-xl"
                    )}
                  >
                    {standfirst}
                  </p>
                ) : null}
              </>

              <>
                {!isAppWebView ? (
                  <div className="md:px-8">
                    <ArticleToolbar
                      shareUrl={article.shortUrl || ""}
                      articleTitle={title}
                      isPremium={isPremium}
                      handleGenerateGiftURLModal={handleGenerateGiftURLModal}
                      setIsModalOpen={setIsModalOpen}
                      alacrityRes={alacrityRes}
                      displayPurchase={purchaseArticleDetails}
                      keyword={tags}
                      cueId={id}
                      isSummaryAvailable={isSummaryAvailable}
                    />
                  </div>
                ) : null}
              </>

              <div ref={displayArticleRef} />

              <>
                {authors && isAuthorOnly ? (
                  <div
                    className={cn(
                      "mt-4 space-y-4",
                      "md:flex md:flex-wrap md:space-y-0",
                      "relative mb-6 min-h-[50px] pt-6 text-gray-850 md:px-8",
                      "before:absolute before:left-0 before:right-0 before:top-0 before:h-[1px] before:bg-gray-175",
                      "md:before:left-8 md:before:right-8"
                    )}
                  >
                    <ArticleByline
                      authors={authors}
                      editedTime={editedTime}
                      updatedTime={publishTime}
                      hasUpdatedTimeDisplayed={hasUpdatedTimeDisplayed}
                      linkParams={
                        new URLSearchParams({ ref: "article-top-byline" })
                      }
                    />
                  </div>
                ) : null}
              </>

              <ArticleMainMedia media={media || []} />

              <>
                {!isAuthorOnly ? (
                  <div className="mb-6 pt-6 md:px-8">
                    <ArticleByline
                      authors={authors || []}
                      editedTime={editedTime}
                      updatedTime={publishTime}
                      hasUpdatedTimeDisplayed={hasUpdatedTimeDisplayed}
                    />
                  </div>
                ) : null}
              </>

              <ArticleAttachments
                attachments={relatedAttachments || []}
                className="mb-6 md:mx-8"
              />

              <>
                {isChinese ? (
                  <ChineseBanner
                    className="mx-auto my-6 md:mx-8"
                    urlPath={article.urlPath}
                  />
                ) : null}
              </>

              <div
                className={cn(
                  "mx-auto my-4 font-lucida text-xl leading-8 -tracking-5% text-gray-850 md:mb-6 md:px-8",
                  {
                    "mb-4 md:mb-6 [&>*:last-child]:relative [&>*:last-child]:after:absolute [&>*:last-child]:after:inset-0 [&>*:last-child]:after:bg-gradient-to-t [&>*:last-child]:after:from-white [&>*:last-child]:after:to-transparent":
                      !isFullContent,
                  }
                )}
                data-testid="article-body-container"
              >
                <ArticleParagraphElements
                  body={article.body || []}
                  article={article}
                  adsWrapperClass={cn(
                    "min-h-[375px] w-screen bg-gray-125 py-6 transition-all duration-300 border-y border-gray-175"
                  )}
                  gsChannels={index === 0 ? gsChannels : gsChannelsAPI}
                  articleIndex={index}
                  isGifted={isGifted}
                  className={"mb-4 md:mb-6"}
                  isBrandedContent={isBrandedContent}
                  isPaidPressRelease={isPaidPressRelease}
                  paragraphIndices={paragraphIndices}
                  isFullContent={isFullContent}
                />
              </div>

              <>{isChinese ? <FeedbackCN className="mb-6 md:mx-8" /> : null}</>

              <>
                {isFullContent ? (
                  <ArticleTags
                    tags={article?.tags}
                    className="mb-6 md:px-8"
                    linkParams={
                      new URLSearchParams({ ref: "article-bottom-keyword" })
                    }
                  />
                ) : null}
              </>

              <>
                {publishTime && isFullContent ? (
                  <div className="no-print mb-6 border-t border-gray-175 md:mx-8">
                    <ArticlePurchaseLink
                      title={title}
                      publishedDate={publishTime}
                      versions={Versions.version2}
                      displayPurchase={purchaseArticleDetails.isDisplayed}
                      urlPath={urlPath}
                    />
                  </div>
                ) : null}
              </>

              <>
                {isSGUser ? (
                  <PosthogFeature flagName="feature_enable_user_login">
                    <DelayedComponentsLocationChange
                      waitBeforeShow={1000}
                      urlPath={urlPath}
                    >
                      <CASBlock
                        cdpId={cdpPaywallModuleId}
                        index={index || 0}
                        className="no-print mt-11 md:px-8 lg:-mx-6"
                        fallback={
                          !isSubscriber ? (
                            <FallbackPaywall
                              className="mb-6 transition duration-300 ease-in data-[closed]:opacity-0"
                              isPremium={isPremium}
                            />
                          ) : null
                        }
                      />
                    </DelayedComponentsLocationChange>
                  </PosthogFeature>
                ) : null}
              </>

              <>
                {!isFullContent ? (
                  <ArticleTags
                    tags={article.tags}
                    className="mb-8 mt-11 md:px-8 lg:-mx-6"
                    linkParams={
                      new URLSearchParams({ ref: "article-bottom-keyword" })
                    }
                  />
                ) : null}

                {mainSection && !isPaidPressRelease ? (
                  <DynamicImu
                    index={index || 0}
                    totalParagraphs={totalParagraphs}
                    adsWrapperClass={cn(
                      "min-h-[375px] w-screen bg-gray-125 py-6 transition-all duration-300 border-y border-gray-175"
                    )}
                    articleId={article?.id}
                    keywords={article?.tags}
                  />
                ) : null}
              </>
            </Container>

            <>
              {removeMoreVariant === VariantType.CONTROL ? (
                <Container rootClassName="flex md:gap-6 px-0 lg:flex-row flex-col-reverse no-print">
                  <>
                    {!isPaidPressRelease && !isBrandedContent ? (
                      <ArticlesFromSameSection
                        currentArticleId={article.id}
                        data={readMoreSectionOrTagData?.items || []}
                        entity={readMoreSectionOrTagData?.entity}
                        linkParams={
                          new URLSearchParams({
                            ref: `article-bottom-more-in-${sections?.[0]?.uniqueName || ""}`,
                          })
                        }
                      />
                    ) : null}
                  </>

                  <>
                    {mostPopularData && !isPaidPressRelease ? (
                      <div className="no-print mx-auto mb-6 lg:min-w-[300px] lg:max-w-[300px]">
                        <MostPopularNew
                          data={mostPopularData}
                          rootClassName="my-5"
                          linkParams={
                            new URLSearchParams({
                              ref: "article-bottom-most-popular",
                            })
                          }
                        />
                      </div>
                    ) : null}
                  </>
                </Container>
              ) : null}
            </>

            <>
              {urlPath && isOutbrainEnabled ? (
                <OutBrainWidget
                  articleUrl={urlPath}
                  widgetId={testOutbrainWidgetId}
                  className="track-impression"
                  dataAttributes={{
                    "data-section-name": "outbrain-recommendation",
                  }}
                />
              ) : null}
            </>
          </Container>
        </div>
      </article>
    </>
  );
};

export default MainNewsDefault;
